import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';
import { Button } from './button';

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  scrollable = false,
  variant = 'center',
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  /**
   * Overlay-scroll layout: the popup becomes a full-screen, top-anchored scroll viewport and the
   * panel grows to its natural height inside it — so a tall form scrolls the overlay (page locked)
   * instead of scrolling inside the card. Matches the design system's Dialog. Defaults to the
   * centered, fixed-height layout to leave existing dialogs unchanged.
   *
   * The panel defaults to `sm:max-w-md`; pass a wider `className` (e.g. `sm:max-w-2xl`) to override.
   */
  scrollable?: boolean;
  /**
   * "takeover" — a bare overlay-scroll shell like `scrollable`, but with no baked-in radius/padding/
   * max-width: the caller supplies the panel's full responsive layout via `className` (and the
   * overlay's own padding via `overlayClassName`). Used by `ResponsiveModal` to render one shell
   * across mobile/desktop breakpoints instead of swapping between `Dialog` and `Sheet`.
   */
  variant?: 'center' | 'takeover';
  /** `variant="takeover"` only — extra classes on the scrolling overlay (e.g. desktop centering padding). */
  overlayClassName?: string;
}) {
  const closeButton = showCloseButton && (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      render={<Button variant="ghost" className="absolute top-4 right-4" size="icon-sm" />}
    >
      <Icon name="x" />
      <span className="sr-only">{'Close'}</span>
    </DialogPrimitive.Close>
  );

  if (variant === 'takeover') {
    return (
      <DialogPortal>
        <DialogOverlay className="bg-[color-mix(in_oklab,var(--foreground)_42%,transparent)] backdrop-blur-none" />
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          className={cn(
            'fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
            overlayClassName,
          )}
          {...props}
        >
          <div className={cn('relative bg-card text-sm text-popover-foreground outline-none', className)}>
            {children}
            {closeButton}
          </div>
        </DialogPrimitive.Popup>
      </DialogPortal>
    );
  }

  if (scrollable) {
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain p-4 pt-[7vh] duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0"
          {...props}
        >
          <div
            className={cn(
              'relative grid h-fit w-full max-w-[calc(100%-2rem)] gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground border sm:max-w-md',
              className,
            )}
          >
            {children}
            {closeButton}
          </div>
        </DialogPrimitive.Popup>
      </DialogPortal>
    );
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground border duration-100 outline-none sm:max-w-md data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {closeButton}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />;
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>{'Close'}</DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-xl leading-tight font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
